/**
 * Provider Logo Component
 *
 * Displays AI provider logos (Anthropic, OpenAI, Google, xAI, Meta, Kimi, Qwen) based on model key.
 * Used across leaderboard, community votes, and results pages.
 */

interface ProviderLogoProps {
  modelKey: string;
  size?: number;
  showBackground?: boolean;
}

export function ProviderLogo({
  modelKey,
  size = 32,
  showBackground = true,
}: ProviderLogoProps) {
  const key = modelKey.toLowerCase();
  const isAnthropic = key.includes('claude') || key.includes('anthropic');
  const isOpenAI = key.includes('gpt') || key.includes('openai');
  const isGoogle = key.includes('gemini') || key.includes('google');
  const isXAI = key.includes('grok');
  const isMeta = key.includes('llama') || key.includes('meta');
  const isKimi = key.includes('kimi') || key.includes('moonshot');
  const isQwen = key.includes('qwen') || key.includes('alibaba');

  const iconSize = size * 0.5;

  // Claude AI - Multi-ray starburst logo with rounded ends
  if (isAnthropic) {
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: showBackground ? '#FDF4F0' : 'transparent',
          border: showBackground ? '1px solid #F5DDD3' : 'none',
        }}
      >
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 100 100"
          fill="none"
        >
          <g stroke="#D97757" strokeWidth="8" strokeLinecap="round">
            {/* 12 rays with varying lengths radiating from center */}
            <line x1="50" y1="50" x2="50" y2="8" />
            <line x1="50" y1="50" x2="75" y2="15" />
            <line x1="50" y1="50" x2="92" y2="35" />
            <line x1="50" y1="50" x2="88" y2="58" />
            <line x1="50" y1="50" x2="78" y2="82" />
            <line x1="50" y1="50" x2="50" y2="92" />
            <line x1="50" y1="50" x2="25" y2="85" />
            <line x1="50" y1="50" x2="10" y2="62" />
            <line x1="50" y1="50" x2="8" y2="38" />
            <line x1="50" y1="50" x2="22" y2="18" />
            <line x1="50" y1="50" x2="38" y2="12" />
            <line x1="50" y1="50" x2="65" y2="10" />
          </g>
        </svg>
      </div>
    );
  }

  // OpenAI - Black knot logo
  if (isOpenAI) {
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: showBackground ? '#F5F5F5' : 'transparent',
          border: showBackground ? '1px solid #E5E5E5' : 'none',
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none">
          <path
            d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4091-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"
            fill="#000"
          />
        </svg>
      </div>
    );
  }

  // Google/Gemini - Blue diamond star
  if (isGoogle) {
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: showBackground ? '#EEF4FF' : 'transparent',
          border: showBackground ? '1px solid #C7DBFF' : 'none',
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z"
            fill="#4285F4"
          />
        </svg>
      </div>
    );
  }

  // xAI/Grok - X logo
  if (isXAI) {
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: showBackground ? '#F0F0F0' : 'transparent',
          border: showBackground ? '1px solid #E0E0E0' : 'none',
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none">
          <path
            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
            fill="#000"
          />
        </svg>
      </div>
    );
  }

  // Meta/Llama - Official Meta logo
  if (isMeta) {
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: showBackground ? '#EBF5FF' : 'transparent',
          border: showBackground ? '1px solid #0467DF' : 'none',
        }}
      >
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
          fill="#0467DF"
        >
          <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z" />
        </svg>
      </div>
    );
  }

  // Kimi/Moonshot - Stylized K with blue dot
  if (isKimi) {
    return (
      <div
        className="rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: showBackground ? '#1A1A2E' : 'transparent',
          border: showBackground ? '1px solid #2D2D44' : 'none',
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none">
          {/* Stylized K */}
          <path
            d="M6 4V20M6 12L14 4M6 12L14 20"
            stroke="#FFFFFF"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Blue dot */}
          <circle cx="18" cy="6" r="3" fill="#3B82F6" />
        </svg>
      </div>
    );
  }

  // Qwen/Alibaba - Official Qwen logo
  if (isQwen) {
    return (
      <div
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: showBackground ? '#F0EEFF' : 'transparent',
          border: showBackground ? '1px solid #665CEE' : 'none',
        }}
      >
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 200 200"
          fill="none"
        >
          <defs>
            <radialGradient id="qwenGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#665CEE" />
              <stop offset="100%" stopColor="#332E91" />
            </radialGradient>
          </defs>
          <path
            d="M174.82 108.75L155.38 75L165.64 57.75C166.46 56.31 166.46 54.53 165.64 53.09L155.38 35.84C154.86 34.91 153.87 34.33 152.78 34.33H114.88L106.14 19.03C105.62 18.1 104.63 17.52 103.54 17.52H83.3C82.21 17.52 81.22 18.1 80.7 19.03L61.26 52.77H41.02C39.93 52.77 38.94 53.35 38.42 54.28L28.16 71.53C27.34 72.97 27.34 74.75 28.16 76.19L45.52 107.5L36.78 122.8C35.96 124.24 35.96 126.02 36.78 127.46L47.04 144.71C47.56 145.64 48.55 146.22 49.64 146.22H87.54L96.28 161.52C96.8 162.45 97.79 163.03 98.88 163.03H119.12C120.21 163.03 121.2 162.45 121.72 161.52L141.16 127.78H158.52C159.61 127.78 160.6 127.2 161.12 126.27L171.38 109.02C172.2 107.58 172.2 105.8 171.38 104.36L174.82 108.75Z"
            fill="url(#qwenGrad)"
          />
          <path
            d="M119.12 163.03H98.88L87.54 144.71H49.64L61.26 126.39H80.7L38.42 55.29H61.26L83.3 19.03L93.56 37.35L83.3 55.29H161.58L151.32 72.54L170.76 106.28H151.32L141.16 88.34L101.18 163.03H119.12Z"
            fill="white"
          />
          <path
            d="M127.86 79.83H76.14L101.18 122.11L127.86 79.83Z"
            fill="url(#qwenGrad)"
          />
        </svg>
      </div>
    );
  }

  // Default fallback
  return (
    <div
      className="rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
      style={{
        width: size,
        height: size,
        background: '#8B8580',
        border: showBackground ? '1px solid #7A756E' : 'none',
      }}
    >
      {modelKey.charAt(0).toUpperCase()}
    </div>
  );
}

// Helper to get provider name from model key
export function getProviderName(modelKey: string): string {
  const key = modelKey.toLowerCase();
  if (key.includes('claude') || key.includes('anthropic')) return 'Anthropic';
  if (key.includes('gpt') || key.includes('openai')) return 'OpenAI';
  if (key.includes('gemini') || key.includes('google')) return 'Google';
  if (key.includes('grok')) return 'xAI';
  if (key.includes('llama') || key.includes('meta')) return 'Meta';
  if (key.includes('kimi') || key.includes('moonshot')) return 'Moonshot';
  if (key.includes('qwen') || key.includes('alibaba')) return 'Alibaba';
  return 'Unknown';
}

// Helper to get provider color
export function getProviderColor(modelKey: string): string {
  const key = modelKey.toLowerCase();
  if (key.includes('claude') || key.includes('anthropic')) return '#D97757';
  if (key.includes('gpt') || key.includes('openai')) return '#000000';
  if (key.includes('gemini') || key.includes('google')) return '#4285F4';
  if (key.includes('grok')) return '#000000';
  if (key.includes('llama') || key.includes('meta')) return '#0467DF';
  if (key.includes('kimi') || key.includes('moonshot')) return '#3B82F6';
  if (key.includes('qwen') || key.includes('alibaba')) return '#665CEE';
  return '#8B8580';
}
